import React from "react";
import { graphql } from "gatsby";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tab from "react-bootstrap/Tab";
import Nav from "react-bootstrap/Nav";

// Components
import AppProvider from "./../components/appProvider";
import Layout from "./../components/layout";
import SEO from "./../components/seo";
import FadeInWhenVisible from "./../components/fadeInWhenVisible";
import Spacer from "./../components/spacer";
import CardHorizontal from "./../components/cardHorizontal";

// Constants
import {
  foodAndDrinksAssets,
  ocioAssets,
  supermarketAssets,
} from "./../modules/constants/index";

// Fallback image for destinations without a specific photo
import fallbackImg from "./../images/common/DJI_0888-min.jpg";

// Types
import { ICardHorizontalProps } from "./../modules/interfaces";

const InfoPage = ({ data, location }: any) => {
  const { t } = useTranslation();
  const { site } = data || [];

  const { siteUrl, phoneRef } = site.siteMetadata;

  const translateFoodAndDrinks: any = t("foodAndDrinks.list", {
    returnObjects: true,
  });

  const translateOcio: any = t("ocio.list", { returnObjects: true });

  const translatSupermarket: any = t("supermarket.list", {
    returnObjects: true,
  });

  return (
    <AppProvider location={location}>
      <Layout>
        <SEO />
        <Helmet>
          <script type="application/ld+json">
            {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "url": "${siteUrl}",
              "name": "Cabinas Vista Lago Arenal",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "${phoneRef}",
                "contactType": "Customer Support"
              }
            }
          `}
          </script>
        </Helmet>
        <section>
          <FadeInWhenVisible>
            <div className="bg-merino">
              <Container>
                <Row>
                  <Col sm={12}>
                    <Spacer>
                      <h1 className="mb-0">{t(`infoHeading`)}</h1>
                    </Spacer>
                  </Col>
                </Row>
              </Container>
              <Container>
                <Row>
                  <Col sm={12}>
                    <Tab.Container defaultActiveKey="activities">
                      <Nav variant="tabs" className="mb-4">
                        <Nav.Item>
                          <Nav.Link eventKey="food">
                            {t(`foodAndDrinks.heading`)}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="activities">
                            {t(`ocio.heading`)}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="supermarkets">
                            {t(`supermarket.heading`)}
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                      <Tab.Content>
                        <Tab.Pane eventKey="food">
                          <Spacer bottomOnly={true}>
                            {translateFoodAndDrinks.map(
                              (item: ICardHorizontalProps, index: number) => {
                                const asset = foodAndDrinksAssets[index] || {};
                                return (
                                  <CardHorizontal
                                    key={index}
                                    img={asset.img || fallbackImg}
                                    alt={item.alt}
                                    heading={item.heading}
                                    time={item.time}
                                    distance={item.distance}
                                    description={item.description}
                                    facebook={asset.facebook}
                                    instagram={asset.instagram}
                                    map={asset.map}
                                    website={asset.website}
                                    youtube={asset.youtube}
                                  />
                                );
                              }
                            )}
                          </Spacer>
                        </Tab.Pane>
                        <Tab.Pane eventKey="activities">
                          <Spacer bottomOnly={true}>
                            {translateOcio.map(
                              (item: ICardHorizontalProps, index: number) => {
                                const asset = ocioAssets[index] || {};
                                return (
                                  <CardHorizontal
                                    key={index}
                                    img={asset.img || fallbackImg}
                                    alt={item.alt}
                                    heading={item.heading}
                                    time={item.time}
                                    distance={item.distance}
                                    description={item.description}
                                    facebook={asset.facebook}
                                    instagram={asset.instagram}
                                    map={asset.map}
                                    website={asset.website}
                                    youtube={asset.youtube}
                                  />
                                );
                              }
                            )}
                          </Spacer>
                        </Tab.Pane>
                        <Tab.Pane eventKey="supermarkets">
                          <Spacer bottomOnly={true}>
                            {translatSupermarket.map(
                              (item: ICardHorizontalProps, index: number) => {
                                const asset = supermarketAssets[index] || {};
                                return (
                                  <CardHorizontal
                                    key={index}
                                    img={asset.img || fallbackImg}
                                    alt={item.alt}
                                    heading={item.heading}
                                    time={item.time}
                                    distance={item.distance}
                                    description={item.description}
                                    facebook={asset.facebook}
                                    instagram={asset.instagram}
                                    map={asset.map}
                                    website={asset.website}
                                    youtube={asset.youtube}
                                  />
                                );
                              }
                            )}
                          </Spacer>
                        </Tab.Pane>
                      </Tab.Content>
                    </Tab.Container>
                  </Col>
                </Row>
              </Container>
            </div>
          </FadeInWhenVisible>
        </section>
      </Layout>
    </AppProvider>
  );
};

export const query = graphql`
  query {
    site {
      siteMetadata {
        title
        description
        author
        siteUrl
        keywords
        image
        menuLinks {
          name
          anchor
          translate
        }
        phone
        phoneRef
        whatsapp
        messenger
        facebook
        instagram
      }
    }
  }
`;

export default InfoPage;
